/**
 * Landed-cost editor — `/purchasing/landed-costs/new` and
 * `/purchasing/landed-costs/:id`.
 *
 * Three steps: cost components → GRN-line selection → server-computed
 * allocation preview and post. The allocation is NEVER computed client-side;
 * `POST /landed-costs/{id}/preview` is a side-effect-free dry run whose rows
 * are rendered verbatim, and the client only verifies Σallocations = Σcosts
 * with exact decimal arithmetic before enabling Post.
 */
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { ChevronDown, ChevronRight, Plus, Trash2 } from "lucide-react";
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Checkbox,
  Combobox,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  toast,
} from "@erp/ui";

import { ConflictDialog } from "@/components/form";
import { MoneyDisplay } from "@/components/money/money-display";
import { formatQuantity } from "@/lib/format";
import { useIdempotentMutation } from "@/features/shared/ops-api";

import {
  LANDED_COST_BASES,
  LANDED_COST_TYPES,
  createLandedCost,
  deleteLandedCost,
  postLandedCost,
  previewLandedCost,
  updateLandedCost,
  type LandedCostBasis,
  type LandedCostDocumentCreate,
  type LandedCostLineInput,
  type LandedCostPreview,
  type LandedCostType,
} from "../api";
import { purchasingKeys, useGrnDetail, useGrnList, useLandedCostDetail } from "../queries";
import { useSupplierNames, useVariantSkus } from "../components/lookup-names";
import { DecimalInput } from "../components/decimal-input";
import { LandedCostStatusBadge } from "../components/status-badges";
import { handleProblem, type FieldErrors } from "../problem-errors";
import { enumLabel } from "../labels";
import {
  AMOUNT_SCALE,
  COST_SCALE,
  FX_SCALE,
  QTY_SCALE,
  addDecimals,
  compareDecimal,
  toScale,
} from "../decimal";

interface CostLineDraft {
  key: string;
  cost_type: LandedCostType;
  charge_reference_code: string;
  allocation_basis: LandedCostBasis;
  amount: string;
  base_amount: string;
}

let costLineSeed = 0;
function newCostLine(): CostLineDraft {
  costLineSeed += 1;
  return {
    key: `cost-${costLineSeed}`,
    cost_type: "freight",
    charge_reference_code: "",
    allocation_basis: "value",
    amount: "",
    base_amount: "",
  };
}

function nowLocalInput(): string {
  const now = new Date();
  return new Date(now.getTime() - now.getTimezoneOffset() * 60_000).toISOString().slice(0, 16);
}

export interface LandedCostEditorPageProps {
  landedCostId?: string;
  canCreate?: boolean;
  canUpdate?: boolean;
  canDelete?: boolean;
  canPost?: boolean;
  canReadGrns?: boolean;
  canReadSuppliers?: boolean;
  canReadVariants?: boolean;
  onCreated: (landedCostId: string) => void;
  onDeleted: () => void;
}

export function LandedCostEditorPage({
  landedCostId,
  canCreate = false,
  canUpdate = false,
  canDelete = false,
  canPost = false,
  canReadGrns = true,
  canReadSuppliers = true,
  canReadVariants = true,
  onCreated,
  onDeleted,
}: LandedCostEditorPageProps) {
  const { t, i18n } = useTranslation();
  const queryClient = useQueryClient();
  const isNew = landedCostId === undefined;

  const detail = useLandedCostDetail(landedCostId);
  const doc = detail.data?.data;
  const posted = doc?.status === "posted";

  const { parties } = useSupplierNames(canReadSuppliers);

  const [supplierId, setSupplierId] = useState<string | null>(null);
  const [currency, setCurrency] = useState("SAR");
  const [baseCurrency, setBaseCurrency] = useState("SAR");
  const [fxRate, setFxRate] = useState("1.0000000000");
  const [invoiceReference, setInvoiceReference] = useState("");
  const [costLines, setCostLines] = useState<CostLineDraft[]>([newCostLine()]);
  const [selectedGrnLineIds, setSelectedGrnLineIds] = useState<string[]>([]);
  const [preview, setPreview] = useState<LandedCostPreview | null>(null);
  const [occurredAt, setOccurredAt] = useState(nowLocalInput());
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
  const [conflict, setConflict] = useState(false);
  const [hydratedId, setHydratedId] = useState<string | null>(null);

  // Hydrate once per loaded document; later edits stay under user control.
  useEffect(() => {
    if (doc === undefined || hydratedId === doc.id) return;
    setHydratedId(doc.id);
    setSupplierId(doc.supplier_id ?? null);
    setCurrency(doc.currency);
    setBaseCurrency(doc.base_currency);
    setFxRate(doc.fx_rate);
    setInvoiceReference(doc.supplier_invoice_reference_token ?? "");
    setCostLines(
      doc.lines.map((line, index) => ({
        key: `cost-existing-${index}`,
        cost_type: line.cost_type,
        charge_reference_code: line.charge_reference_code,
        allocation_basis: line.allocation_basis,
        amount: line.amount,
        base_amount: line.base_amount,
      })),
    );
    setSelectedGrnLineIds(doc.allocations.map((allocation) => allocation.grn_line_id));
  }, [doc, hydratedId]);

  const etag = detail.data?.etag ?? null;

  const supplierOptions = useMemo(
    () => parties.map((party) => ({ value: party.id, label: party.display_name })),
    [parties],
  );

  const totalAmount = addDecimals(
    costLines.map((line) => (line.amount === "" ? "0.0000" : line.amount)),
    AMOUNT_SCALE,
  );
  const baseTotalAmount = addDecimals(
    costLines.map((line) => (line.base_amount === "" ? "0.0000" : line.base_amount)),
    AMOUNT_SCALE,
  );

  const invalidate = (): void => {
    void queryClient.invalidateQueries({ queryKey: purchasingKeys.landedCosts.all });
  };

  const onMutationError = (error: unknown): void => {
    handleProblem(error, t, {
      onConflict: () => setConflict(true),
      onFieldErrors: setFieldErrors,
    });
  };

  const buildLines = (): LandedCostLineInput[] =>
    costLines.map((line, index) => ({
      line_number: index + 1,
      cost_type: line.cost_type,
      charge_reference_code: line.charge_reference_code,
      allocation_basis: line.allocation_basis,
      amount: toScale(line.amount === "" ? "0" : line.amount, AMOUNT_SCALE),
      base_amount: toScale(line.base_amount === "" ? "0" : line.base_amount, AMOUNT_SCALE),
    }));

  const createMutation = useIdempotentMutation<
    Awaited<ReturnType<typeof createLandedCost>>,
    LandedCostDocumentCreate
  >({
    mutationFn: (input, key) => createLandedCost(input, key),
    onSuccess: ({ data }) => {
      setFieldErrors({});
      invalidate();
      onCreated(data.id);
    },
    onError: onMutationError,
  });

  const saveMutation = useMutation({
    mutationFn: () =>
      updateLandedCost(
        landedCostId as string,
        {
          version: doc?.version ?? 1,
          ...(supplierId === null ? {} : { supplier_id: supplierId }),
          currency: currency.toUpperCase(),
          base_currency: baseCurrency.toUpperCase(),
          fx_rate: toScale(fxRate, FX_SCALE),
          total_amount: totalAmount,
          base_total_amount: baseTotalAmount,
          ...(invoiceReference === ""
            ? {}
            : { supplier_invoice_reference_token: invoiceReference }),
          lines: buildLines(),
        },
        etag ?? "",
      ),
    onSuccess: () => {
      setFieldErrors({});
      setPreview(null);
      invalidate();
      void detail.refetch();
    },
    onError: onMutationError,
  });

  const deleteMutation = useMutation({
    mutationFn: () => deleteLandedCost(landedCostId as string),
    onSuccess: () => {
      invalidate();
      onDeleted();
    },
    onError: onMutationError,
  });

  const previewMutation = useMutation({
    mutationFn: () =>
      previewLandedCost(landedCostId as string, { grn_line_ids: selectedGrnLineIds }),
    onSuccess: (result) => {
      setFieldErrors({});
      setPreview(result);
    },
    onError: (error) => {
      setPreview(null);
      onMutationError(error);
    },
  });

  const postMutation = useIdempotentMutation<
    Awaited<ReturnType<typeof postLandedCost>>,
    { occurred_at: string; grn_line_ids: string[] }
  >({
    mutationFn: (input, key) => postLandedCost(landedCostId as string, input, etag ?? "", key),
    onSuccess: () => {
      invalidate();
      void detail.refetch();
      toast({ title: t("purchasing.landedCost.posted", "Landed cost posted.") });
    },
    onError: onMutationError,
  });

  const create = (): void => {
    setFieldErrors({});
    createMutation.mutate({
      ...(supplierId === null ? {} : { supplier_id: supplierId }),
      currency: currency.toUpperCase(),
      base_currency: baseCurrency.toUpperCase(),
      fx_rate: toScale(fxRate, FX_SCALE),
      total_amount: totalAmount,
      base_total_amount: baseTotalAmount,
      ...(invoiceReference === "" ? {} : { supplier_invoice_reference_token: invoiceReference }),
      lines: buildLines(),
    });
  };

  const reconciles =
    preview !== null &&
    compareDecimal(preview.allocated_total, baseTotalAmount, AMOUNT_SCALE) === 0;

  const renderQty = (value: string, digits = QTY_SCALE): string => {
    try {
      return formatQuantity(value, i18n.language, digits);
    } catch {
      return value;
    }
  };

  const editable = isNew ? canCreate : canUpdate && !posted;

  if (!isNew && detail.isLoading) {
    return (
      <p className="p-4 text-sm text-muted-foreground">
        {t("purchasing.common.loading", "Loading…")}
      </p>
    );
  }
  if (!isNew && detail.isError) {
    return (
      <div className="flex flex-col items-start gap-3 p-4">
        <p role="alert" className="text-sm text-destructive">
          {t("purchasing.landedCost.loadError", "This landed cost could not be loaded.")}
        </p>
        <Button type="button" variant="outline" onClick={() => void detail.refetch()}>
          {t("actions.retry")}
        </Button>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4 p-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="space-y-1">
          <h1 className="flex flex-wrap items-center gap-2 text-xl font-semibold">
            {isNew ? (
              t("purchasing.landedCost.newTitle", "New landed cost")
            ) : (
              <>
                <span dir="ltr" className="font-mono">
                  {doc?.document_number}
                </span>
                {doc !== undefined ? <LandedCostStatusBadge status={doc.status} /> : null}
              </>
            )}
          </h1>
          <ol className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
            <li>{t("purchasing.landedCost.step1", "1 · Costs")}</li>
            <li aria-hidden="true">›</li>
            <li>{t("purchasing.landedCost.step2", "2 · Select receipt lines")}</li>
            <li aria-hidden="true">›</li>
            <li>{t("purchasing.landedCost.step3", "3 · Preview and post")}</li>
          </ol>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {isNew && canCreate ? (
            <Button type="button" loading={createMutation.isPending} onClick={create}>
              {t("purchasing.landedCost.createDraft", "Create draft")}
            </Button>
          ) : null}
          {!isNew && editable ? (
            <Button
              type="button"
              loading={saveMutation.isPending}
              onClick={() => saveMutation.mutate()}
            >
              {t("purchasing.landedCost.save", "Save draft")}
            </Button>
          ) : null}
          {!isNew && canDelete && !posted ? (
            <Button
              type="button"
              variant="ghost"
              loading={deleteMutation.isPending}
              onClick={() => deleteMutation.mutate()}
            >
              {t("purchasing.landedCost.delete", "Delete draft")}
            </Button>
          ) : null}
        </div>
      </div>

      {posted ? (
        <p className="rounded-md border bg-muted/40 p-3 text-sm">
          {t("purchasing.landedCost.postedReadOnly", "A posted landed cost is immutable.")}
        </p>
      ) : null}

      <Card>
        <CardHeader>
          <CardTitle>
            <h2>{t("purchasing.landedCost.costsTitle", "① Cost components")}</h2>
          </CardTitle>
        </CardHeader>
        <CardContent className="flex flex-col gap-4">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="purchasing-lc-supplier-field">
                {t("purchasing.landedCost.supplier", "Supplier")}
              </Label>
              <Combobox
                id="purchasing-lc-supplier-field"
                options={supplierOptions}
                value={supplierId}
                onChange={setSupplierId}
                disabled={!editable}
                placeholder={t("purchasing.common.pickSupplier", "Choose a supplier…")}
                searchPlaceholder={t("purchasing.common.searchSupplier", "Search supplier…")}
                emptyMessage={t("purchasing.common.noSuppliers", "No suppliers found.")}
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="purchasing-lc-currency">
                {t("purchasing.landedCost.currency", "Currency")}
              </Label>
              <Input
                id="purchasing-lc-currency"
                dir="ltr"
                maxLength={3}
                value={currency}
                disabled={!editable}
                onChange={(event) => setCurrency(event.target.value.toUpperCase())}
                aria-invalid={fieldErrors.currency !== undefined}
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="purchasing-lc-base-currency">
                {t("purchasing.landedCost.baseCurrency", "Base currency")}
              </Label>
              <Input
                id="purchasing-lc-base-currency"
                dir="ltr"
                maxLength={3}
                value={baseCurrency}
                disabled={!editable}
                onChange={(event) => setBaseCurrency(event.target.value.toUpperCase())}
                aria-invalid={fieldErrors.base_currency !== undefined}
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="purchasing-lc-fx">
                {t("purchasing.landedCost.fxRate", "FX rate")}
              </Label>
              <DecimalInput
                id="purchasing-lc-fx"
                scale={FX_SCALE}
                value={fxRate}
                disabled={!editable}
                onChange={setFxRate}
                aria-invalid={fieldErrors.fx_rate !== undefined}
              />
            </div>
            <div className="flex flex-col gap-1.5 sm:col-span-2">
              <Label htmlFor="purchasing-lc-invoice-reference">
                {t("purchasing.landedCost.invoiceReference", "Supplier invoice reference")}
              </Label>
              <Input
                id="purchasing-lc-invoice-reference"
                dir="ltr"
                value={invoiceReference}
                disabled={!editable}
                onChange={(event) => setInvoiceReference(event.target.value)}
              />
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full min-w-[44rem] text-sm">
              <thead>
                <tr className="border-b text-xs uppercase text-muted-foreground">
                  <th scope="col" className="p-2 text-start">
                    {t("purchasing.landedCost.line.type", "Type")}
                  </th>
                  <th scope="col" className="p-2 text-start">
                    {t("purchasing.landedCost.line.chargeCode", "Charge code")}
                  </th>
                  <th scope="col" className="p-2 text-start">
                    {t("purchasing.landedCost.line.basis", "Basis")}
                  </th>
                  <th scope="col" className="p-2 text-end">
                    {t("purchasing.landedCost.line.amount", "Amount")}
                  </th>
                  <th scope="col" className="p-2 text-end">
                    {t("purchasing.landedCost.line.baseAmount", "Base amount")}
                  </th>
                  <th scope="col" className="p-2" />
                </tr>
              </thead>
              <tbody>
                {costLines.map((line, index) => (
                  <tr key={line.key} className="border-b align-top">
                    <td className="p-2">
                      <Select
                        value={line.cost_type}
                        disabled={!editable}
                        onValueChange={(value) =>
                          setCostLines((current) =>
                            current.map((candidate) =>
                              candidate.key === line.key
                                ? { ...candidate, cost_type: value as LandedCostType }
                                : candidate,
                            ),
                          )
                        }
                      >
                        <SelectTrigger
                          className="w-36"
                          aria-label={t("purchasing.landedCost.line.typeOf", "Cost type {{n}}", {
                            n: index + 1,
                          })}
                        >
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {LANDED_COST_TYPES.map((value) => (
                            <SelectItem key={value} value={value}>
                              {enumLabel(t, "landedCostType", value)}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </td>
                    <td className="p-2">
                      <Input
                        aria-label={t(
                          "purchasing.landedCost.line.chargeCodeOf",
                          "Charge code {{n}}",
                          { n: index + 1 },
                        )}
                        dir="ltr"
                        className="w-40"
                        value={line.charge_reference_code}
                        disabled={!editable}
                        onChange={(event) =>
                          setCostLines((current) =>
                            current.map((candidate) =>
                              candidate.key === line.key
                                ? { ...candidate, charge_reference_code: event.target.value }
                                : candidate,
                            ),
                          )
                        }
                        aria-invalid={
                          fieldErrors[`lines[${index}].charge_reference_code`] !== undefined
                        }
                      />
                    </td>
                    <td className="p-2">
                      <Select
                        value={line.allocation_basis}
                        disabled={!editable}
                        onValueChange={(value) =>
                          setCostLines((current) =>
                            current.map((candidate) =>
                              candidate.key === line.key
                                ? { ...candidate, allocation_basis: value as LandedCostBasis }
                                : candidate,
                            ),
                          )
                        }
                      >
                        <SelectTrigger
                          className="w-32"
                          aria-label={t("purchasing.landedCost.line.basisOf", "Basis {{n}}", {
                            n: index + 1,
                          })}
                        >
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {LANDED_COST_BASES.map((value) => (
                            <SelectItem key={value} value={value}>
                              {enumLabel(t, "landedCostBasis", value)}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </td>
                    <td className="p-2 text-end">
                      <DecimalInput
                        aria-label={t("purchasing.landedCost.line.amountOf", "Amount {{n}}", {
                          n: index + 1,
                        })}
                        className="w-32 text-end"
                        scale={AMOUNT_SCALE}
                        value={line.amount}
                        disabled={!editable}
                        onChange={(next) =>
                          setCostLines((current) =>
                            current.map((candidate) =>
                              candidate.key === line.key
                                ? { ...candidate, amount: next }
                                : candidate,
                            ),
                          )
                        }
                        aria-invalid={fieldErrors[`lines[${index}].amount`] !== undefined}
                      />
                      {fieldErrors[`lines[${index}].amount`] !== undefined ? (
                        <p role="alert" className="mt-1 text-xs text-destructive">
                          {fieldErrors[`lines[${index}].amount`]}
                        </p>
                      ) : null}
                    </td>
                    <td className="p-2 text-end">
                      <DecimalInput
                        aria-label={t(
                          "purchasing.landedCost.line.baseAmountOf",
                          "Base amount {{n}}",
                          { n: index + 1 },
                        )}
                        className="w-32 text-end"
                        scale={AMOUNT_SCALE}
                        value={line.base_amount}
                        disabled={!editable}
                        onChange={(next) =>
                          setCostLines((current) =>
                            current.map((candidate) =>
                              candidate.key === line.key
                                ? { ...candidate, base_amount: next }
                                : candidate,
                            ),
                          )
                        }
                        aria-invalid={fieldErrors[`lines[${index}].base_amount`] !== undefined}
                      />
                    </td>
                    <td className="p-2 text-end">
                      {editable && costLines.length > 1 ? (
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          aria-label={t("purchasing.landedCost.line.remove", "Remove cost {{n}}", {
                            n: index + 1,
                          })}
                          onClick={() =>
                            setCostLines((current) =>
                              current.filter((candidate) => candidate.key !== line.key),
                            )
                          }
                        >
                          <Trash2 className="size-4" aria-hidden="true" />
                        </Button>
                      ) : null}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="flex flex-wrap items-center justify-between gap-3">
            {editable ? (
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setCostLines((current) => [...current, newCostLine()])}
              >
                <Plus className="size-4" aria-hidden="true" />
                {t("purchasing.landedCost.addCost", "Add cost")}
              </Button>
            ) : (
              <span />
            )}
            <dl className="flex flex-wrap items-center gap-x-6 text-sm">
              <div className="flex items-center gap-2">
                <dt className="text-muted-foreground">
                  {t("purchasing.landedCost.total", "Total")}
                </dt>
                <dd data-testid="landed-cost-total">
                  <MoneyDisplay value={{ amount: totalAmount, currency }} />
                </dd>
              </div>
              <div className="flex items-center gap-2">
                <dt className="text-muted-foreground">
                  {t("purchasing.landedCost.baseTotal", "Base total")}
                </dt>
                <dd data-testid="landed-cost-base-total">
                  <MoneyDisplay value={{ amount: baseTotalAmount, currency: baseCurrency }} />
                </dd>
              </div>
            </dl>
          </div>
        </CardContent>
      </Card>

      {!isNew ? (
        <Card>
          <CardHeader>
            <CardTitle>
              <h2>{t("purchasing.landedCost.selectTitle", "② Select receipt lines")}</h2>
            </CardTitle>
          </CardHeader>
          <CardContent className="flex flex-col gap-3">
            {canReadGrns ? (
              <GrnLineSelector
                supplierId={supplierId}
                canReadVariants={canReadVariants}
                selected={selectedGrnLineIds}
                disabled={posted}
                onToggle={(grnLineId) =>
                  setSelectedGrnLineIds((current) =>
                    current.includes(grnLineId)
                      ? current.filter((candidate) => candidate !== grnLineId)
                      : [...current, grnLineId],
                  )
                }
              />
            ) : (
              <p className="text-sm text-muted-foreground">
                {t(
                  "purchasing.landedCost.grnPermission",
                  "Reading goods receipts requires purchasing.grn.read.",
                )}
              </p>
            )}
            <p className="text-xs text-muted-foreground">
              {t("purchasing.landedCost.selectedCount", "{{count}} receipt line(s) selected", {
                count: selectedGrnLineIds.length,
              })}
            </p>
          </CardContent>
        </Card>
      ) : null}

      {!isNew ? (
        <Card>
          <CardHeader className="flex-row items-center justify-between gap-3">
            <CardTitle>
              <h2>{t("purchasing.landedCost.previewTitle", "③ Preview and post")}</h2>
            </CardTitle>
            <div className="flex flex-wrap items-center gap-2">
              <Button
                type="button"
                variant="outline"
                loading={previewMutation.isPending}
                disabled={selectedGrnLineIds.length === 0 || previewMutation.isPending}
                onClick={() => previewMutation.mutate()}
              >
                {t("purchasing.landedCost.preview", "Recalculate preview")}
              </Button>
              {canPost && !posted ? (
                <Button
                  type="button"
                  loading={postMutation.isPending}
                  disabled={preview === null || !reconciles || postMutation.isPending}
                  onClick={() =>
                    postMutation.mutate({
                      occurred_at: new Date(occurredAt).toISOString(),
                      grn_line_ids: selectedGrnLineIds,
                    })
                  }
                >
                  {t("purchasing.landedCost.post", "Post landed cost")}
                </Button>
              ) : null}
            </div>
          </CardHeader>
          <CardContent className="flex flex-col gap-3">
            <div className="flex max-w-xs flex-col gap-1.5">
              <Label htmlFor="purchasing-lc-occurred">
                {t("purchasing.landedCost.occurredAt", "Posting date")}
              </Label>
              <Input
                id="purchasing-lc-occurred"
                type="datetime-local"
                dir="ltr"
                value={occurredAt}
                disabled={posted}
                onChange={(event) => setOccurredAt(event.target.value)}
                aria-invalid={fieldErrors.occurred_at !== undefined}
              />
              {fieldErrors.occurred_at !== undefined ? (
                <p role="alert" className="text-xs text-destructive">
                  {fieldErrors.occurred_at}
                </p>
              ) : null}
            </div>

            {preview === null ? (
              <p className="text-sm text-muted-foreground">
                {t(
                  "purchasing.landedCost.previewEmpty",
                  "Select receipt lines and recalculate to see the server's allocation.",
                )}
              </p>
            ) : (
              <>
                <div className="overflow-x-auto">
                  <table className="w-full min-w-[52rem] text-sm">
                    <thead>
                      <tr className="border-b text-xs uppercase text-muted-foreground">
                        <th scope="col" className="p-2 text-start">
                          {t("purchasing.landedCost.previewColumns.line", "Receipt line")}
                        </th>
                        <th scope="col" className="p-2 text-start">
                          {t("purchasing.landedCost.previewColumns.basis", "Basis")}
                        </th>
                        <th scope="col" className="p-2 text-end">
                          {t("purchasing.landedCost.previewColumns.share", "Share %")}
                        </th>
                        <th scope="col" className="p-2 text-end">
                          {t("purchasing.landedCost.previewColumns.allocated", "Allocated")}
                        </th>
                        <th scope="col" className="p-2 text-end">
                          {t("purchasing.landedCost.previewColumns.priorCost", "Prior unit cost")}
                        </th>
                        <th scope="col" className="p-2 text-end">
                          {t(
                            "purchasing.landedCost.previewColumns.revisedCost",
                            "Revised unit cost",
                          )}
                        </th>
                        <th scope="col" className="p-2 text-end">
                          {t("purchasing.landedCost.previewColumns.cogs", "COGS variance")}
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {preview.allocations.map((allocation) => (
                        <tr
                          key={`${allocation.landed_cost_line_id}/${allocation.grn_line_id}`}
                          className="border-b"
                        >
                          <td className="p-2">
                            <code dir="ltr" className="rounded bg-muted px-1.5 py-0.5 text-xs">
                              {allocation.grn_line_id.slice(0, 8)}
                            </code>
                          </td>
                          <td className="p-2">
                            {enumLabel(t, "landedCostBasis", allocation.allocation_basis)}
                          </td>
                          <td dir="ltr" className="p-2 text-end tabular-nums">
                            {renderQty(allocation.basis_share_pct)}
                          </td>
                          <td className="p-2 text-end">
                            <MoneyDisplay
                              value={{
                                amount: allocation.allocated_amount,
                                currency: preview.base_currency,
                              }}
                            />
                          </td>
                          <td dir="ltr" className="p-2 text-end tabular-nums">
                            {renderQty(allocation.prior_unit_cost, COST_SCALE)}
                          </td>
                          <td dir="ltr" className="p-2 text-end tabular-nums">
                            {renderQty(allocation.revised_unit_cost, COST_SCALE)}
                          </td>
                          <td className="p-2 text-end">
                            <MoneyDisplay
                              value={{
                                amount: allocation.cogs_variance_amount,
                                currency: preview.base_currency,
                              }}
                            />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <dl className="flex flex-wrap items-center gap-x-6 gap-y-1 text-sm">
                  <div className="flex items-center gap-2">
                    <dt className="text-muted-foreground">
                      {t("purchasing.landedCost.previewColumns.allocatedTotal", "Allocated total")}
                    </dt>
                    <dd data-testid="landed-cost-allocated-total">
                      <MoneyDisplay
                        value={{
                          amount: preview.allocated_total,
                          currency: preview.base_currency,
                        }}
                      />
                    </dd>
                  </div>
                  <div className="flex items-center gap-2">
                    <dt className="text-muted-foreground">
                      {t(
                        "purchasing.landedCost.previewColumns.revaluation",
                        "Inventory revaluation",
                      )}
                    </dt>
                    <dd>
                      <MoneyDisplay
                        value={{
                          amount: preview.inventory_revaluation_total,
                          currency: preview.base_currency,
                        }}
                      />
                    </dd>
                  </div>
                  <div className="flex items-center gap-2">
                    <dt className="text-muted-foreground">
                      {t("purchasing.landedCost.previewColumns.rounding", "Rounding remainder")}
                    </dt>
                    <dd>
                      <MoneyDisplay
                        value={{
                          amount: preview.rounding_remainder_total,
                          currency: preview.base_currency,
                        }}
                      />
                    </dd>
                  </div>
                </dl>

                <p
                  role="status"
                  data-testid="landed-cost-reconciliation"
                  className={
                    reconciles ? "text-sm text-muted-foreground" : "text-sm text-destructive"
                  }
                >
                  {reconciles
                    ? t(
                        "purchasing.landedCost.reconciles",
                        "Σ allocations equals Σ costs — ready to post.",
                      )
                    : t(
                        "purchasing.landedCost.doesNotReconcile",
                        "Σ allocations does not equal Σ costs — posting is blocked.",
                      )}
                </p>
              </>
            )}
          </CardContent>
        </Card>
      ) : null}

      <ConflictDialog
        open={conflict}
        onReload={() => {
          setConflict(false);
          setHydratedId(null);
          void detail.refetch();
        }}
        onDismiss={() => setConflict(false)}
      />
    </div>
  );
}

/** Expandable GRN → GRN-line picker; details load only when a row is opened. */
function GrnLineSelector({
  supplierId,
  canReadVariants,
  selected,
  disabled,
  onToggle,
}: {
  supplierId: string | null;
  canReadVariants: boolean;
  selected: string[];
  disabled: boolean;
  onToggle: (grnLineId: string) => void;
}) {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState<string | null>(null);
  const grns = useGrnList(
    supplierId === null || supplierId === "" ? {} : { supplier_id: supplierId },
    null,
    25,
  );

  if (grns.isLoading) {
    return (
      <p className="text-sm text-muted-foreground">{t("purchasing.common.loading", "Loading…")}</p>
    );
  }
  if (grns.rows.length === 0) {
    return (
      <p className="text-sm text-muted-foreground">
        {t(
          "purchasing.landedCost.noEligibleGrns",
          "No posted goods receipts are eligible. Only posted receipts in the document's base currency can be allocated.",
        )}
      </p>
    );
  }

  return (
    <ul className="flex flex-col divide-y">
      {grns.rows.map((grn) => (
        <li key={grn.id} className="py-2">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="h-auto justify-start gap-2 p-1"
            onClick={() => setExpanded((current) => (current === grn.id ? null : grn.id))}
          >
            {expanded === grn.id ? (
              <ChevronDown className="size-4" aria-hidden="true" />
            ) : (
              <ChevronRight className="size-4 rtl:-scale-x-100" aria-hidden="true" />
            )}
            <span dir="ltr" className="font-mono">
              {grn.grn_number}
            </span>
            <Badge variant="outline">{grn.received_at.slice(0, 10)}</Badge>
          </Button>
          {expanded === grn.id ? (
            <GrnLines
              grnId={grn.id}
              canReadVariants={canReadVariants}
              selected={selected}
              disabled={disabled}
              onToggle={onToggle}
            />
          ) : null}
        </li>
      ))}
    </ul>
  );
}

function GrnLines({
  grnId,
  canReadVariants,
  selected,
  disabled,
  onToggle,
}: {
  grnId: string;
  canReadVariants: boolean;
  selected: string[];
  disabled: boolean;
  onToggle: (grnLineId: string) => void;
}) {
  const { t } = useTranslation();
  const detail = useGrnDetail(grnId);
  const { variantSku } = useVariantSkus(canReadVariants);
  if (detail.isLoading) {
    return (
      <p className="ps-6 text-xs text-muted-foreground">
        {t("purchasing.common.loading", "Loading…")}
      </p>
    );
  }
  if (detail.data === undefined) {
    return (
      <p role="alert" className="ps-6 text-xs text-destructive">
        {t("purchasing.landedCost.grnLoadError", "Receipt lines could not be loaded.")}
      </p>
    );
  }
  return (
    <ul className="ms-6 mt-1 flex flex-col gap-1">
      {detail.data.lines.map((line) => (
        <li key={line.id} className="flex items-center gap-2 text-sm">
          <Checkbox
            id={`grn-line-${line.id}`}
            checked={selected.includes(line.id)}
            disabled={disabled}
            onCheckedChange={() => onToggle(line.id)}
          />
          <Label htmlFor={`grn-line-${line.id}`} className="flex items-center gap-2 font-normal">
            <span dir="ltr" className="font-mono">
              {variantSku(line.variant_id)}
            </span>
            <span dir="ltr" className="text-muted-foreground">
              {line.qty_accepted}
            </span>
          </Label>
        </li>
      ))}
    </ul>
  );
}
